/**
 * KA + M order entry — the one place Manage's frontend decides what a delivery
 * slot name *means* (keep the canonical slot value in API payloads).
 *
 * TT-862 Phase 6C: orders in the database carry more than one spelling of the
 * same window, because the Customer App and Manage kept separate vocabularies
 * until this phase aligned them. Those spellings are read-compatibility facts —
 * existing orders carry them and must stay readable and priceable forever — so
 * every surface resolves a stored name to its canonical value here rather than
 * matching the text itself. `canonicalDeliverySlot` is that resolution, and it
 * is the only alias table in the frontend: `orderDeliveryPricing.js` (the night
 * fee), `driverI18n.js` (the driver's slot line) and the order screens all ask
 * this module instead of keeping a second list that can drift out of date.
 *
 * Mirrors `packages/shared/delivery_slots.py`, which is what checkout prices
 * from — the value set, the alias table and the casing/spacing rule are the
 * same on both sides on purpose.
 */

// The durable values, written to `orders.delivery_slot`.
export const MORNING_DELIVERY_SLOT = "11AM-1:30PM";
export const NOON_DELIVERY_SLOT = "2:30-5PM";
export const EVENING_DELIVERY_SLOT = "5-7:30PM";
export const NIGHT_DELIVERY_SLOT = "7:30-11PM";

/**
 * The one slot with no window of its own: its delivery time comes from the
 * order's `custom_time`. Named here rather than spelled `"Custom"` at each
 * call site, because that string is a fee input — see `isNightDeliverySlot`
 * in `orderDeliveryPricing.js` and `custom_time` on `/calculate-distance`.
 * Mirrors `packages/shared/delivery_slots.py::CUSTOM_SLOT`.
 */
export const CUSTOM_DELIVERY_SLOT = "Custom";

export const CANONICAL_DELIVERY_SLOTS = [
  MORNING_DELIVERY_SLOT,
  NOON_DELIVERY_SLOT,
  EVENING_DELIVERY_SLOT,
  NIGHT_DELIVERY_SLOT,
];

/**
 * Every spelling ever written to an order, mapped to the value it means.
 *
 * Read-compatibility entries, not choices: nothing new is written with a key
 * from this table. Mirrors `delivery_slots.SLOT_ALIASES`.
 */
export const DELIVERY_SLOT_ALIASES = {
  // Customer App vocabulary before Phase 6C aligned it.
  "2:30PM-5PM": NOON_DELIVERY_SLOT,
  "5PM-7:30PM": EVENING_DELIVERY_SLOT,
  "8PM-11PM": NIGHT_DELIVERY_SLOT,
  // Late-night variants seen from order intake.
  "7:30-11:00PM": NIGHT_DELIVERY_SLOT,
  "7:30PM-11PM": NIGHT_DELIVERY_SLOT,
  "8-11PM": NIGHT_DELIVERY_SLOT,
  "8:00PM-11PM": NIGHT_DELIVERY_SLOT,
  "8PM-11:00PM": NIGHT_DELIVERY_SLOT,
};

/** Casing and spacing carry no meaning in a slot name. */
function lookupKey(value) {
  return value.toLowerCase().replace(/\s/g, "");
}

const SLOT_BY_LOOKUP_KEY = new Map();
for (const canonical of [...CANONICAL_DELIVERY_SLOTS, CUSTOM_DELIVERY_SLOT]) {
  SLOT_BY_LOOKUP_KEY.set(lookupKey(canonical), canonical);
}
for (const [alias, target] of Object.entries(DELIVERY_SLOT_ALIASES)) {
  const key = lookupKey(alias);
  if (!SLOT_BY_LOOKUP_KEY.has(key)) SLOT_BY_LOOKUP_KEY.set(key, target);
}

/**
 * The canonical value a slot name means, or `null` when nothing recognises it.
 *
 * `null` is a real answer and callers must treat it as one: an unrecognised
 * slot is a window this code cannot place on a clock, and guessing one would
 * put a time in front of a customer that the kitchen never promised.
 */
export function canonicalDeliverySlot(slot) {
  if (typeof slot !== "string") return null;
  const text = slot.trim();
  if (!text) return null;
  return SLOT_BY_LOOKUP_KEY.get(lookupKey(text)) ?? null;
}

export function isCustomDeliverySlot(slot) {
  return canonicalDeliverySlot(slot) === CUSTOM_DELIVERY_SLOT;
}

export function isNightDeliveryWindowSlot(slot) {
  return canonicalDeliverySlot(slot) === NIGHT_DELIVERY_SLOT;
}

export const DELIVERY_SLOT_WINDOW_LABELS = {
  [MORNING_DELIVERY_SLOT]: "MORNING",
  [NOON_DELIVERY_SLOT]: "NOON",
  [EVENING_DELIVERY_SLOT]: "EVENING",
  [NIGHT_DELIVERY_SLOT]: "NIGHT",
  [CUSTOM_DELIVERY_SLOT]: "CUSTOM",
};

/** The MORNING/NOON/EVENING/NIGHT/CUSTOM word for a slot, in any spelling. */
export function deliverySlotWindowLabel(slot) {
  const canonical = canonicalDeliverySlot(slot);
  if (!canonical) return "";
  return DELIVERY_SLOT_WINDOW_LABELS[canonical] || "";
}

/**
 * The slot as staff read it in a picker: the window name plus the canonical
 * value. A legacy spelling is shown as the value it means, so one order grid
 * never displays two names for the same window.
 */
export function deliverySlotSelectLabel(slot) {
  const name = deliverySlotWindowLabel(slot);
  if (!name) return slot;
  return `${name} · ${canonicalDeliverySlot(slot)}`;
}
